// /frontend/src/pages/account/DesignRequestsPage.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Layout from '../../components/layout/Layout';
import Spinner from '../../components/ui/Spinner';
import { uploadService } from '../../services/uploadService'; // Fixed import

const DesignRequestsPage = () => {
  const { user } = useSelector(state => state.auth);
  
  const [designRequests, setDesignRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDesignRequests = async () => {
      try {
        const data = await uploadService.getUserDesignRequests(); // Fixed method call
        setDesignRequests(data);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load design requests');
      } finally {
        setLoading(false);
      }
    };

    fetchDesignRequests();
  }, []);

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'review':
        return 'bg-purple-100 text-purple-800';
      case 'revision':
        return 'bg-orange-100 text-orange-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <Spinner />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">My Design Requests</h1>
          <Link 
            to="/custom-design" 
            className="bg-teal-800 text-white px-4 py-2 rounded hover:bg-teal-900 transition-all"
          >
            New Design Request
          </Link>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md mb-6">
            <p>{error}</p>
          </div>
        )}

        {designRequests.length === 0 ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
            <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path>
            </svg>
            <h2 className="text-xl font-semibold mb-2">No Design Requests Yet</h2>
            <p className="text-gray-600 mb-4">You haven't submitted any design requests yet.</p>
            <Link 
              to="/custom-design" 
              className="inline-flex items-center bg-teal-800 text-white px-6 py-2 rounded hover:bg-teal-900 transition-all"
            >
              Create Your First Design Request
            </Link>
          </div>
        ) : (
          <div className="bg-white overflow-hidden shadow rounded-lg divide-y divide-gray-200">
            <div className="hidden sm:grid sm:grid-cols-6 bg-gray-50 px-6 py-3">
              <div className="font-medium text-gray-700">ID</div>
              <div className="font-medium text-gray-700">Package</div>
              <div className="font-medium text-gray-700">Status</div>
              <div className="font-medium text-gray-700">Created At</div>
              <div className="font-medium text-gray-700">Payment</div>
              <div className="font-medium text-gray-700 text-right">Actions</div>
            </div>
            <div className="divide-y divide-gray-200">
              {designRequests.map(request => (
                <div key={request._id} className="px-6 py-4 sm:grid sm:grid-cols-6 sm:gap-4">
                  <div className="flex flex-col space-y-1">
                    <span className="sm:hidden font-medium text-gray-500">ID:</span>
                    <span className="truncate">{request._id.substring(0, 8)}...</span>
                  </div>
                  <div className="mt-2 sm:mt-0 flex flex-col space-y-1">
                    <span className="sm:hidden font-medium text-gray-500">Package:</span>
                    <span className="capitalize">{request.packageType}</span>
                  </div>
                  <div className="mt-2 sm:mt-0 flex flex-col space-y-1">
                    <span className="sm:hidden font-medium text-gray-500">Status:</span>
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(request.status)} capitalize`}>
                      {request.status.replace('_', ' ')}
                    </span>
                  </div>
                  <div className="mt-2 sm:mt-0 flex flex-col space-y-1">
                    <span className="sm:hidden font-medium text-gray-500">Created At:</span>
                    <span>{new Date(request.createdAt).toLocaleDateString()}</span>
                  </div>
                  <div className="mt-2 sm:mt-0 flex flex-col space-y-1">
                    <span className="sm:hidden font-medium text-gray-500">Payment:</span>
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      request.paymentStatus === 'paid' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-orange-100 text-orange-800'
                    } capitalize`}>
                      {request.paymentStatus}
                    </span>
                  </div>
                  <div className="mt-4 sm:mt-0 flex justify-end">
                    <Link 
                      to={`/custom-design/request/${request._id}`} 
                      className="text-teal-600 hover:text-teal-900 mr-4"
                    >
                      View
                    </Link>
                    
                    {request.paymentStatus === 'pending' && (
                      <Link 
                        to={`/checkout/design-request/${request._id}`} 
                        className="text-teal-600 hover:text-teal-900"
                      >
                        Pay
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default DesignRequestsPage;